import React, { useState, useEffect, useRef } from 'react';

import { Box } from '@wds/box';
import { Zoom } from '@wds/motion';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';

import PropTypes from 'prop-types';
import TOTPEnrollment from './components/TOTPEnrollment';
import Challenge from './components/Challenge';
import EnrollmentComplete from './components/EnrollmentComplete';
import EmailEnrollment from './components/EmailEnrollment';
import RecoveryEmailSetup from './components/RecoveryEmailSetup';
import RecoveryEmailChallenge from './components/RecoveryEmailChallenge';

import STRINGS from './constants';
import { clearCooldownTimer, startCooldownTimer } from './utils/cooldown';

import { isSafeRedirectUrl } from '../../../sma.common/utils';

const RESEND_COOLDOWN_SEC = 120;

const getCookie = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
	return '';
};

const methodMap = {
	0: 'totp',
	1: 'email',
};

const parseJsonIfPresent = async (response) => {
	const contentType = response.headers.get('content-type') || '';
	if (!contentType.includes('application/json')) {
		return null;
	}

	try {
		return await response.json();
	} catch (e) {
		return null;
	}
};

const mapToMfaErrorKey = (errorMessage) => {
	if (!errorMessage) {
		return 'unknownError';
	}

	if (errorMessage === 'not_authenticated') {
		return 'session_expired';
	}

	if (typeof errorMessage === 'string' && STRINGS?.[errorMessage]) {
		return errorMessage;
	}

	const text = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
	if (
		text.includes('SyntaxError')
		|| text.includes('Unexpected token <')
		|| text.includes('did not match the expected pattern')
	) {
		return 'session_stale_refresh';
	}

	if (text.includes('not_authenticated')) {
		return 'session_expired';
	}

	return 'unknownError';
};

const MFA = (props) => {
	const {
		context,
		setContext,
		isModal,
		handleClose,
		setCurrentView,
		isChange,
		onOpenRecoveryModal,
		onRecoveryEmailVerified,
	} = props;
	const defaultContext = {
		challengeCode: '',
		isRememberDevice: false,
		qrCodeString: '',
		securityKeyString: '',
		status: '',
		mfa_enabled: context?.mfa_enabled || false,
		mfa_methods: context?.mfa_methods || [],
		changeInProgress: false,
		needsRecoveryEmail: false,
	};

	const [mfaContext, setMfaContext] = useState(defaultContext);
	const [method, setMethod] = useState('');
	const [methods, setMethods] = useState();
	const [isLoaded, setIsLoaded] = useState(false);
	const [currentPage, setCurrentPage] = useState();
	const [error, setError] = useState('');
	const [nextUrl, setNextUrl] = useState('');
	const [isSliding, setIsSliding] = useState(false);
	const [isZooming, setIsZooming] = useState(false);
	const [sending, setSending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [message, setMessage] = useState('');
	const [pendingRecoveryEmailAddress, setPendingRecoveryEmailAddress] = useState('');
	const [recoveryEmailCooldown, setRecoveryEmailCooldown] = useState(0);
	const [recoveryEmailSetupStep, setRecoveryEmailSetupStep] = useState('enter-email');
	const [recoveryEmailResetSignal, setRecoveryEmailResetSignal] = useState(0);
	const [hasRecoveryEmail, setHasRecoveryEmail] = useState(false);
	const sentOnceRef = useRef(false);
	const timerRef = useRef(null);
	const cooldownEndAtRef = useRef(0);
	const navigationHandledRef = useRef(false); // Track if we've handled recovery navigation
	const isBootstrappingRef = useRef(true); // Avoid stale context-driven page changes before API bootstrap resolves

	const handleOpenRecoveryPage = (newMethod) => {
		if (onOpenRecoveryModal) {
			onOpenRecoveryModal(newMethod);
		} else {
			setCurrentPage('recovery-email-challenge');
		}
	};

	useEffect(() => {
		setIsLoaded(true);
		if (!isChange) {
			getNextUrl();
		}
		getEnrolledMethods();
		clearCooldownTimer(timerRef);
		
		// Cleanup interval on unmount
		return () => {
			clearCooldownTimer(timerRef);
		};
	}, []);

	useEffect(() => {
		if (setContext) {
			if (!mfaContext) return;
			// Only update parent fields when values actually change.
			const nextEnabled = mfaContext.mfa_enabled || mfaContext.mfa_methods?.length > 0;
			const nextMethods = mfaContext.mfa_methods || [];
			setContext((prev) => {
				if (prev?.mfa_enabled === nextEnabled && prev?.mfa_methods === nextMethods) {
					return prev;
				}
				return {
					...prev,
					mfa_enabled: nextEnabled,
					mfa_methods: nextMethods,
				};
			});
		}
	}, [mfaContext.mfa_enabled, mfaContext.mfa_methods]); // Only watch specific fields

	// Watch for parent context changes (e.g., after recovery email verification)
	useEffect(() => {
		if (isBootstrappingRef.current) {
			return;
		}

		if (context && context.changeInProgress) {
			console.log('[MFA] Context change detected, changeInProgress:', context);
			// Always clear any previous error state so old messages don't persist
			setError('');

			if (!navigationHandledRef.current) {
				navigationHandledRef.current = true; // Mark as handled
				// Update internal state with enrollment data from parent
				setMfaContext((prev) => ({
					...prev,
					...context,
				}));
			}

			// Navigate to appropriate enrollment page
			if (context.qrCodeString) {
				// TOTP enrollment
				console.log('[MFA] Navigating to TOTP enrollment');
				setCurrentPage('totp-enrollment');
				setMethod('totp');
			} else if (context.status === 'code_pending') {
				// Email enrollment
				console.log('[MFA] Navigating to Email enrollment');
				setCurrentPage('email-enrollment');
				setMethod('email');
			}
		}
	}, [context?.changeInProgress, context?.qrCodeString, context?.status]); // Only watch specific change indicators

	useEffect(() => {
		if (method === 'email' && !sentOnceRef.current) {
			if (isChange) {
				sentOnceRef.current = true;
				if (mfaContext?.status === 'code_pending') {
					setMessage(STRINGS.email_sent);
					startCooldown();
				}
			}
			if (!isChange && currentPage === 'challenge') {
				sendEmailCode();
			}
		}
	}, [method]);

	useEffect(() => {
		setMessage('');
		if (currentPage === 'totp-enrollment') {
			if (!mfaContext.qrCodeString) {
				getSecurityKey();
			}
			setMethod('totp');
			// Track method selection
			if (window.track) {
				window.track('mfaMethodSelected', {
					mfaMethod: 'totp',
					methodType: 'authenticator_app',
					methodName: 'Authenticator App',
					selectionContext: isChange ? 'settings' : 'login'
				});
			}
		} else if (currentPage === 'email-enrollment') {
			setMethod('email');
			// Track method selection
			if (window.track) {
				window.track('mfaMethodSelected', {
					mfaMethod: 'email',
					methodType: 'email_verification',
					methodName: 'Email Verification',
					selectionContext: isChange ? 'settings' : 'login'
				});
			}
		} else if (currentPage === 'challenge' && method === 'email' && !sentOnceRef.current) {
			sendEmailCode();
		}
	}, [currentPage]);

	const getNextUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('next')) {
			setNextUrl(urlParams.get('next'));
		}
	};

	const getEnrolledMethods = async () => {
		try {
			const response = await fetch(reverse('api-internal-mfa-enroll'), {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
			});
			const data = await parseJsonIfPresent(response);

			if (!response.ok) {
				if (response.status === 403) {
					setError('session_stale_refresh');
					return;
				}

				if (response.status === 401) {
					if (data?.error === 'invalid_code_locked_out') {
						window.location.href = reverse('mfa-lockout');
						return;
					}

					if (data?.error === 'not_authenticated') {
						if (isChange) {
							window.location.href = reverse('mfa-lockout');
						} else {
							window.location.href = '/acc/l/';
						}
						return;
					}
				}
				throw new Error(data?.error || 'unable_to_load_mfa_state');
			}

			if (!data) {
				throw new Error('unable_to_load_mfa_state');
			}

			const successData = data;

			const enrolledMethods = successData.enrolled_methods || [];
			console.log('enrolledMethods', enrolledMethods);
			const changeInProgress = successData.change_in_progress || false;

			// Get pending email code info
			const emailCodePending = successData.email_code_pending || false;
			const cooldownRemaining = successData.cooldown_remaining || 0;
			const pendingMethodId = successData.pending_method_id || null;
			const hasPendingEmailEnrollmentMethod = Boolean(pendingMethodId);
			const shouldResumeEmailEnrollment = isChange
				? (changeInProgress && hasPendingEmailEnrollmentMethod)
				: (hasPendingEmailEnrollmentMethod || emailCodePending);
			setMfaContext((prev) => ({
				...prev,
				mfa_methods: enrolledMethods,
				changeInProgress,
				// If there's a pending email enrollment method, preserve its state.
				...(shouldResumeEmailEnrollment && {
					status: emailCodePending ? 'code_already_pending' : 'code_pending',
					method_id: pendingMethodId,
					cooldown_remaining: cooldownRemaining,
				}),
			}));

			// If there's a pending email code, start cooldown and only show the
			// "already sent" message while resend is still locked.
			if (emailCodePending && shouldResumeEmailEnrollment) {
				if(cooldownRemaining > 0) {
					startCooldown(cooldownRemaining);
					setMessage(STRINGS.code_already_pending);
				}
			}
			const pendingRecoveryEmail = successData.pending_recovery_email || false;
			setHasRecoveryEmail(successData.has_recovery_email || false);

			if (pendingRecoveryEmail) {
				setPendingRecoveryEmailAddress(successData.pending_recovery_email_address || '');
				setRecoveryEmailCooldown(successData.recovery_email_cooldown || 0);
			}

			setMethods(enrolledMethods);
			if (pendingRecoveryEmail && !isChange) {
				setCurrentPage('recovery-email-setup');
			} else if (shouldResumeEmailEnrollment) {
				// Preserve pending email-code flow when modal is reopened.
				// For change-device flow, only restore email enrollment when
				// there is a pending unverified email enrollment method.
				setMethod('email');
				setCurrentPage('email-enrollment');
			} else if (isChange && changeInProgress) {
				// Show the intermediate challenge/confirmation screen so the user
				// sees the "change in progress" banner and clicks Confirm to
				// resume enrollment.
				const enrolledMethod = enrolledMethods.length > 0
					? methodMap[enrolledMethods[0].method]
					: 'totp';
				setMethod(enrolledMethod);
				setCurrentPage('challenge');
			} else if (isModal) {
				if (enrolledMethods.length === 1) {
					setMethod(methodMap[enrolledMethods[0].method]);
					setCurrentPage('challenge');
				} else if (enrolledMethods.length > 1) {
					setCurrentPage('method-selection');
				} else {
					setCurrentPage('totp-enrollment');
				}
			} else if (enrolledMethods.some((m) => m.method === 0 || m.method === 1)) {
				const resolvedMethod = methodMap[enrolledMethods[0].method];
				setMethod(resolvedMethod);
				if (resolvedMethod === 'email' && !isChange) {
					await sendEmailCode();
				}
				setCurrentPage('challenge');
			} else {
				setCurrentPage('totp-enrollment');
			}
		} catch (err) {
			handleError(`Error fetching enrolled methods: ${err}`);
		} finally {
			isBootstrappingRef.current = false;
		}
	};

	const startCooldown = (remaining) => {
		startCooldownTimer({
			remaining,
			defaultSeconds: RESEND_COOLDOWN_SEC,
			setCooldown,
			timerRef,
			endAtRef: cooldownEndAtRef,
		});
	};

	const sendEmailCode = async () => {
		if (sending || cooldown > 0) return;
		
		// Track resend code click (if not first send)
		if (sentOnceRef.current && window.track) {
			window.track('mfaCodeResent', {
				method: 'email',
				context: isChange ? 'settings' : 'login'
			});
		}
		
		setSending(true);
		setMessage('');
		setError('');
		try {
			let url;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
			} else {
				url = reverse('api-internal-mfa-challenge');
			}
			const resp = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action: 'send_code', method: 'email' }),
				credentials: 'same-origin',
			});
			const data = await resp.json();
			if (data.success) {
				setMessage(data.message);
				sentOnceRef.current = true;
				startCooldown();
			} else if (data.message) {
				setMessage(STRINGS?.[data.message]);
			} else if (data.error) {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setMfaContext({
						...mfaContext,
						challengeCode: '',
						status: 'code_already_pending',
					});
					setError('');
					setMessage(STRINGS.code_already_pending);
					startCooldown(data.time_remaining);
				} else {
					setError(STRINGS?.[data.error] || STRINGS.unknownError);
				}
			} else {
				setMessage(STRINGS.unknownError);
			}
		} catch (err) {
			console.log(err);
			handleError(STRINGS.unknownError);
		} finally {
			setSending(false);
		}
	};

	const getSecurityKey = async () => {
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'setup_totp';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method: 'totp' }),
			});
			 // Check response.ok before reading the body
			if(!response.ok) {
				await handleErrorResponse(response);
				return;
			}
			const data = await response.json();
			if (data.error) {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else {
					setError(data.error);
				}
			} else {
				setMfaContext((prev) => ({
					...prev,
					method_id: data.method_id || prev.method_id,
					qrCodeString: data.qr_code,
					securityKeyString: data.secret || '',
				}));
			}
		} catch (err) {
			handleError(`${STRINGS.errorFetchingSecurityKey}: ${err}`);
		}
	};

	const handleErrorResponse = async (response) => {
		const errorData = await parseJsonIfPresent(response);

		if (response.status === 403) {
			if (errorData?.error) {
				setError(errorData.error);
				if (errorData.error === 'too_many_attempts' || errorData.error === 'invalid_code_locked_out') {
					setCurrentView('locked-out');
				}
				return;
			}
			setError('session_stale_refresh');
			return;
		}

		if (response.status === 401) {
			if (errorData?.error === 'invalid_code_locked_out' || errorData?.error === 'too_many_attempts') {
				setCurrentView('locked-out');
				setError(errorData.error);
				return;
			}

			setError('session_expired');
			return;
		}

		if (response.status === 400) {
			setError(errorData?.error || 'unknownError');
			if (errorData?.error === 'too_many_attempts' || errorData?.error === 'invalid_code_locked_out') {
				setCurrentView('locked-out');
			}
			return;
		}

		setError('unknownError');
	};

	const handleError = (errorMessage) => {
		setError(mapToMfaErrorKey(errorMessage));
	};

	const handleChangeMethod = async () => {
		console.log('[MFA] handleChangeMethod called. isChange:', isChange, 'changeInProgress:', mfaContext.changeInProgress, 'context.changeInProgress:', context?.changeInProgress);
		
		// During enrollment pages in change flow, toggling methods must always call backend
		// so method_id and challenge state stay in sync.
		const isEnrollmentPage = currentPage === 'totp-enrollment' || currentPage === 'email-enrollment';
		const isDeviceChange = isChange && (
			isEnrollmentPage ||
			mfaContext.changeInProgress ||
			context?.changeInProgress
		);
		
		if (isDeviceChange) {
			const newMethod = currentPage === 'totp-enrollment' ? 'email' : 'totp';
			console.log('[MFA] Changing method during device change to:', newMethod);
			
			try {
				// Call begin_device_change with the new method
				const response = await fetch(reverse('api-internal-mfa-change-device'), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRFToken': getCookie('csrftoken'),
					},
					body: JSON.stringify({
						action: 'begin_device_change',
						method: newMethod,
						use_recovery_email: true, // We're already verified via recovery email
					}),
				});
				const data = await response.json();
				console.log('[MFA] Change method response:', data);
				
				if (data.success) {
					// Reset navigation handler so it can process the new method
					navigationHandledRef.current = false;
					
					// Update context with new enrollment data
					let newContext = { ...mfaContext };
					if (data.method.method === 'totp') {
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							qrCodeString: data.qr_code,
							securityKeyString: data.secret,
							changeInProgress: true,
						};
						setCurrentPage('totp-enrollment');
						setMethod('totp');
					} else if (data.method.method === 'email') {
						const emailCodePending = data.message === 'email_code_pending';
						const status = emailCodePending ? 'code_already_pending' : 'code_pending';
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							status,
							cooldown_remaining: data.time_remaining || 0,
							changeInProgress: true,
						};
						setCurrentPage('email-enrollment');
						setMethod('email');
						if (emailCodePending) {
							setMessage(STRINGS.code_already_pending);
							if (data.time_remaining > 0) {
								startCooldown(data.time_remaining);
							}
						} else {
							setMessage(STRINGS.email_sent);
							startCooldown();
						}
					}
					setMfaContext(newContext);
					setError('');
				} else if (data.error === 'code_already_pending') {
					// Special handling for email code already pending during method switch
					// This can happen if switching to email and a code was recently sent
					console.log('[MFA] Code already pending, navigating to email enrollment');
					const newContext = {
						...mfaContext,
						challengeCode: '',
						new_method: newMethod,
						method_id: data.method?.id,
						status: 'code_already_pending',
						cooldown_remaining: data.time_remaining,
						changeInProgress: true,
					};
					setMfaContext(newContext);
					setCurrentPage('email-enrollment');
					setMethod('email');
					setMessage(STRINGS.code_already_pending);
					if (data.time_remaining > 0) {
						startCooldown(data.time_remaining);
					}
					setError('');
				} else {
					setError(data.error || 'Failed to change method');
				}
			} catch (err) {
				handleError(`Error changing method: ${err}`);
			}
		} else {
			// Normal method toggle (not during device change)
			setCurrentPage(currentPage === 'totp-enrollment' ? 'email-enrollment' : 'totp-enrollment');
			setMfaContext((prev) => ({
				// Reset challenge and method identifiers so verify request can't use stale method_id.
				...prev,
				challengeCode: '',
				method_id: null,
			}));
			setError('');
		}
	};

	const handleTryNewQR = async () => {
		await getSecurityKey();
	};

	const handleOnFinish = () => {
		if (!isModal) {
			if (isSafeRedirectUrl(nextUrl)) {
				window.location.replace(nextUrl);
			} else {
				window.location.replace('/');
			}
		} else {
			handleClose();
		}
	};

	const handleResetRecoveryEmailSetup = async () => {
		// Keep backend pending recovery-email state intact so entering the same
		// email can resume existing pending-code flow without sending a new code.
		setPendingRecoveryEmailAddress('');
		setRecoveryEmailCooldown(0);
		setRecoveryEmailSetupStep('enter-email');
		setRecoveryEmailResetSignal((prev) => prev + 1);
	};

	return (
		<>
			{!isModal && currentPage === 'recovery-email-setup' && recoveryEmailSetupStep === 'verify-code' && (
				<Box mb={2}>
					<Typography
						variant='link'
						color='link'
						component='button'
						type='button'
						onClick={handleResetRecoveryEmailSetup}
						id='recovery-email-setup-cancel'
						aria-label='recovery-email-setup-cancel'
						style={{
							background: 'none',
							border: 'none',
							padding: 0,
							cursor: 'pointer',
						}}
					>
						{STRINGS.useDifferentRecoveryEmail || 'Use a different recovery email'}
					</Typography>
				</Box>
			)}
			{!isModal && currentPage !== 'enrollment-complete' && currentPage !== 'recovery-email-setup' && (
				<Box mb={2}>
					<Typography
						variant='link'
						color='link'
						component='a'
						href='/acc/lo/'
						id='logout'
						aria-label='logout'
					>
						{STRINGS.cancelAndLogout}
					</Typography>
				</Box>
			)}
			<Box
				id='mfa-card-1'
				display='flex'
				justifyContent='center'
				alignItems='center'
				role='main'
				aria-labelledby='login-page-title'
				style={{ boxShadow: isModal ? undefined : '0px 2px 8px rgba(0, 0, 0, 0.1)' }}
			>
				<Box
					display='flex'
					justifyContent='space-around'
				>
					<Zoom
						in
					>
						<Box>
							<Box
								style={{
									width: '400px',
									backgroundColor: isModal ? undefined : 'white',
									margin: isModal ? 0 : 'auto',
									minHeight: 320,
								}}
								py='4'
								px='6'
								role='region'
							>
								<Zoom
									in={isLoaded}
									onEnter={() => setIsZooming(true)}
									onExited={() => setIsZooming(false)}
								>
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'
									>
										<Box
											display='flex'
											justifyContent='center'
											alignItems='center'
											style={{ width: '400px' }}
										>
											<img
												className='sma-logo-powered'
												src='/media/img/smapply/apply.svg'
												alt='SurveyMonkey Apply logo'
												style={{ maxWidth: '150px', margin: 25 }}
											/>
										</Box>
									</Box>
									<Box
										style={{
											minHeight: currentPage === 'enrollment-complete' ? 'auto' : 320,
											position: isSliding || isZooming ? 'relative' : 'static',
										}}
									>
										{!currentPage && (
										<Box
											display='flex'
											flexDirection='column'
											justifyContent='center'
											alignItems='center'
											gap={2}
											mt={9}
										>
											<ProgressCircle continuous />
										</Box>
									)}
									{currentPage === 'method-selection' && (
											<Box
												display='flex'
												flexDirection='column'
												alignItems='center'
												gap={4}
												mt={2}
											>
												<Typography variant='sectionTitleSm' align='center' weight='medium'>
													{STRINGS.selectMFAMethod}
												</Typography>
												<Typography variant='bodySm' align='center'>
													{STRINGS.selectMFAMethodMessage}
												</Typography>
												{methods && methods.map((m) => (
													<Button
														key={`method-selection-${m.method}`}
														onClick={
															() => {
																setMethod(methodMap[m.method]);
																setCurrentPage('challenge');
															}
														}
														id={`method-selection-${m.method}`}
														size='lg'
														style={{ width: '200px' }}
													>
														{m.method === 0 && STRINGS.totpMethod}
														{m.method === 1 && STRINGS.emailMethod}
													</Button>
												))}
											</Box>
										)}
										{currentPage === 'totp-enrollment' && (
											<TOTPEnrollment
												isLoaded={isLoaded}
												context={mfaContext}
												setContext={setMfaContext}
												error={error}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleChangeMethod={handleChangeMethod}
												handleErrorResponse={handleErrorResponse}
												handleError={handleError}
												handleTryNewQR={handleTryNewQR}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												isChange={isChange}
												isModal={isModal}
												handleOnFinish={handleOnFinish}
											/>
										)}
										{currentPage === 'email-enrollment' && (
											<EmailEnrollment
												isLoaded={isLoaded}
												context={mfaContext}
												setContext={setMfaContext}
												error={error}
												setError={setError}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleChangeMethod={handleChangeMethod}
												handleError={handleError}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												isChange={isChange}
												cooldown={cooldown}
												startCooldown={startCooldown}
												isModal={isModal}
												handleOnFinish={handleOnFinish}
											/>
										)}
										{currentPage === 'challenge' && (
											<Challenge
												isLoaded={isLoaded}
												context={mfaContext}
												setContext={setMfaContext}
												error={error}
												setError={setError}
												message={message}
												setMessage={setMessage}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleErrorResponse={handleErrorResponse}
												handleError={handleError}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												nextUrl={nextUrl}
												method={method}
												isChange={isChange}
												sending={sending}
												cooldown={cooldown}
												sendEmailCode={sendEmailCode}
												startCooldown={startCooldown}
												onOpenRecoveryModal={hasRecoveryEmail ? handleOpenRecoveryPage : null}
											/>
										)}
										{currentPage === 'recovery-email-challenge' && (
											<RecoveryEmailChallenge
												isLoaded={isLoaded}
												currentPage={currentPage}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												setCurrentPage={setCurrentPage}
												handleError={handleError}
												nextUrl={nextUrl}
												context={mfaContext}
												setContext={setMfaContext}
											/>
										)}
									{currentPage === 'recovery-email-setup' && (
    										<RecoveryEmailSetup
        									isLoaded={isLoaded}
        									currentPage={currentPage}
       										isSliding={isSliding}
       										setIsSliding={setIsSliding}
        									handleOnFinish={handleOnFinish}
        									handleError={handleError}
											initialPendingEmail={pendingRecoveryEmailAddress}
											initialCooldown={recoveryEmailCooldown}
											resetSignal={recoveryEmailResetSignal}
												onSetupStateChange={setRecoveryEmailSetupStep}
											onRecoveryEmailVerified={onRecoveryEmailVerified}
											isModal={isModal}
    									/>
								)}
										{currentPage === 'enrollment-complete' && (
											<EnrollmentComplete
												isLoaded={isLoaded}
												context={mfaContext}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												currentPage={currentPage}
												handleOnFinish={handleOnFinish}
												finishBtnTxt={isModal ? STRINGS.finish : STRINGS.continueToSite}
												nextUrl={nextUrl}
											/>
										)}
									</Box>
								</Zoom>
							</Box>
						</Box>
					</Zoom>
				</Box>
			</Box>
		</>
	);
};

MFA.propTypes = {
	setContext: PropTypes.func,
	isModal: PropTypes.bool,
	handleClose: PropTypes.func,
	setCurrentView: PropTypes.func,
	isChange: PropTypes.bool,
	onRecoveryEmailVerified: PropTypes.func,
};

MFA.defaultProps = {
	setContext: null,
	isModal: false,
	handleClose: () => {},
	setCurrentView: () => {},
	isChange: false,
	onRecoveryEmailVerified: null,
};
export default MFA;
